'use client'

import { useCallback, useEffect, useState } from 'react'
import { getCookie } from 'cookies-next'
import { LOYALTY_TOKEN_COOKIE, LOYALTY_TOKEN_EXPIRES_COOKIE } from '~/lib/auth-cookies'

export const LOGIN_STATE_KEY = 'loyalty_login_state'
export const LOGIN_RETURN_TO_KEY = 'loyalty_login_return_to'

const POLL_INTERVAL_MS = 2000
// Refresh a bit before actual expiry so a request in flight never races the token dying mid-call.
const REFRESH_MARGIN_MS = 60_000
const LOYALTY_MEMBER_URL = process.env.NEXT_PUBLIC_LOYALTY_MEMBER_URL

function readToken(): string | null {
  const raw = getCookie(LOYALTY_TOKEN_COOKIE)
  return typeof raw === 'string' && raw.length > 0 ? raw : null
}

function readExpiresAt(): number | null {
  const raw = getCookie(LOYALTY_TOKEN_EXPIRES_COOKIE)
  const num = raw ? Number(raw) : NaN
  return Number.isFinite(num) ? num : null
}

async function callRefresh(): Promise<boolean> {
  const res = await fetch('/api/auth/refresh', { method: 'POST' })
  return res.ok
}

// Web Locks API prevents two tabs from refreshing at the same time — per the
// integration guide, reusing an already-rotated refresh_token revokes the
// entire chain, forcing a full re-login. Falls back to a direct call on
// browsers without navigator.locks (older Safari) — an acceptable, rare
// degradation rather than a hard requirement.
function refreshWithLock(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return (navigator as any).locks.request('loyalty-refresh', callRefresh)
  }
  return callRefresh()
}

/**
 * Reactive Loyalty Member session (Login Handoff flow). Exposes the current
 * access token and keeps it fresh via /api/auth/refresh, without callers
 * needing to think about expiry or cross-tab refresh races.
 */
export function useLoyaltySession() {
  const [token, setToken] = useState<string | null>(() => readToken())

  useEffect(() => {
    const check = () => {
      const next = readToken()
      setToken((prev) => (prev === next ? prev : next))
    }

    check()
    const interval = setInterval(check, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', check)
    window.addEventListener('focus', check)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('focus', check)
    }
  }, [])

  useEffect(() => {
    if (!token) return

    const expiresAt = readExpiresAt()
    if (!expiresAt) return

    const delay = Math.max(expiresAt - Date.now() - REFRESH_MARGIN_MS, 0)
    const timer = setTimeout(() => {
      refreshWithLock().then((ok) => setToken(ok ? readToken() : null))
    }, delay)

    return () => clearTimeout(timer)
  }, [token])

  const login = useCallback(() => {
    if (!LOYALTY_MEMBER_URL) {
      // NEXT_PUBLIC_LOYALTY_MEMBER_URL not configured yet for this environment.
      console.error('NEXT_PUBLIC_LOYALTY_MEMBER_URL is not set — cannot start login handoff.')
      return
    }

    const state = crypto.randomUUID()
    sessionStorage.setItem(LOGIN_STATE_KEY, state)
    sessionStorage.setItem(LOGIN_RETURN_TO_KEY, window.location.href)

    const url = new URL('/login', LOYALTY_MEMBER_URL)
    url.searchParams.set('redirect_uri', `${window.location.origin}/auth/callback`)
    url.searchParams.set('state', state)
    window.location.href = url.toString()
  }, [])

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {})
    setToken(null)
    window.location.reload()
  }, [])

  return { token, isLoggedIn: !!token, login, logout }
}
